import type { FunctionComponent } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  PostStatus,
  type IPost,
} from "../../../types/models/post.models.types";
import { useToast } from "../../../hooks/use-toast";
import { handleHttpError, useHandleNavigate } from "../../../utils/utils";
import usePostStore from "../../store/post.store";
import Button from "./button.core";
import { useApproveDraftHttp } from "../../hooks/http/posts/approve-draft.hook";
import { useApprovePostHttp } from "../../hooks/http/posts/approve-post.hook";
import { useUnflagPostHttp } from "../../hooks/http/posts/unflag-posts.hook";

type AppProps = {
  post: IPost;
};

export const PostAction: FunctionComponent<AppProps> = ({ post }) => {
  const { execute: approveDraftHttp, fetching: fetchingApproveDraft } =
    useApproveDraftHttp();

  const { execute: approvePostHttp, fetching: fetchingApprovePost } =
    useApprovePostHttp();
  const { execute: unFlagPostHttp, fetching: unflaggingPost } =
    useUnflagPostHttp();
  const { removeById } = usePostStore();
  const location = useLocation();
  const { handleNavigate } = useHandleNavigate(
    location.pathname.replace(`/${post.id}`, "")
  );
  const navigate = useNavigate();
  const { toast } = useToast();

  async function approveDraft() {
    const response = await approveDraftHttp({
      body: null,
      pathParams: { id: post.id },
    });
    if (!response.success) {
      return handleHttpError(response, toast);
    }
    removeById(post.id);
    handleNavigate();
  }

  async function approvePost() {
    const response = await approvePostHttp({
      body: null,
      pathParams: { id: post.id },
    });
    if (!response.success) {
      return handleHttpError(response, toast);
    }
    removeById(post.id);
    handleNavigate();
  }

  function rejectDraft() {
    if (location.pathname.endsWith(post.id)) {
      return navigate(`${location.pathname}/draft-reject`);
    }

    navigate(`${location.pathname}/${post.id}/draft-reject`);
  }

  function rejectPost() {
    if (location.pathname.endsWith(post.id)) {
      return navigate(`${location.pathname}/post-reject`);
    }
    navigate(`${location.pathname}/${post.id}/post-reject`);
  }

  function disqualifyPost() {
    if (location.pathname.endsWith(post.id)) {
      return navigate(`${location.pathname}/disqualify`);
    }
    navigate(`${location.pathname}/${post.id}/disqualify`);
  }

  function flagPost() {
    if (location.pathname.endsWith(post.id)) {
      return navigate(`${location.pathname}/flag`);
    }
    navigate(`${location.pathname}/${post.id}/flag`);
  }

  async function unFlagPost() {
    const response = await unFlagPostHttp({
      pathParams: { id: post.id },
      body: null,
    });
    if (!response.success) {
      return handleHttpError(response, toast);
    }
    removeById(post.id);
    document.getElementById("closeSheet")?.click()
  }

  function getActionMetadata(post: IPost) {
    switch (post.postStatus.status) {
      case PostStatus.draft:
        return {
          button1: {
            action: approveDraft,
            fetching: fetchingApproveDraft,
            title: "Approve",
            varient: "primary",
            className:
              "bg-approvebuttonbg border-approvebuttonborder text-approvebuttontext",
          },
          button2: {
            action: rejectDraft,
            fetching: false,
            title: "Reject",
            varient: "red",
            className:
              "bg-rejectbuttonbg border-rejectbuttonborder text-rejectbuttontext",
          },
        };
      case PostStatus.draftApproved:
        return {
          button1: {
            action: rejectDraft,
            fetching: false,
            varient: "red",
            title: "Change to rejected",
            className:
              "bg-rejectbuttonbg border-rejectbuttonborder text-rejectbuttontext",
          },
        };
      case PostStatus.draftRejected:
        return {
          button1: {
            action: approveDraft,
            fetching: fetchingApproveDraft,
            title: "Change to approved",
            varient: "primary",
            className:
              "bg-approvebuttonbg border-approvebuttonborder text-approvebuttontext",
          },
        };
      case PostStatus.post:
        return {
          button1: {
            action: approvePost,
            fetching: fetchingApprovePost,
            title: "Approve",
            varient: "primary",
            className:
              "bg-approvebuttonbg border-approvebuttonborder text-approvebuttontext",
          },
          button2: {
            action: rejectPost,
            fetching: false,
            title: "Reject",
            varient: "red",
            className:
              "bg-rejectbuttonbg border-rejectbuttonborder text-rejectbuttontext",
          },
        };
      case PostStatus.postApproved:
        return {
          button1: {
            action: flagPost,
            fetching: false,
            title: "Flag",
            varient: "yellow",
            className:
              "bg-tertiarybuttonbg border-tertiarybuttonborder text-tertiarybuttontext",
          },
          button2: {
            action: disqualifyPost,
            fetching: false,
            title: "Disqualify",
            varient: "red",
            className:
              "bg-rejectbuttonbg border-rejectbuttonborder text-rejectbuttontext",
          },
        };
      case PostStatus.postRejected:
        return {
          button1: {
            action: approvePost,
            fetching: fetchingApprovePost,
            title: "Change to approved",
            varient: "primary",
            className:
              "bg-approvebuttonbg border-approvebuttonborder text-approvebuttontext",
          },
        };
      case PostStatus.disqualify:
        return {
          button1: {
            action: approvePost,
            fetching: fetchingApprovePost,
            title: "Change to approved",
            varient: "primary",
            className:
              "bg-approvebuttonbg border-approvebuttonborder text-approvebuttontext",
          },
        };
      case PostStatus.flagged:
        return {
          button1: {
            action: unFlagPost,
            fetching: unflaggingPost,
            title: "Unflag",
            varient: "yellow",
            className:
              "bg-tertiarybuttonbg border-tertiarybuttonborder text-tertiarybuttontext",
          },
          button2: {
            action: disqualifyPost,
            fetching: false,
            title: "Disqualify",
            varient: "red",
            className:
              "bg-rejectbuttonbg border-rejectbuttonborder text-rejectbuttontext",
          },
        };
      case PostStatus.flaggedReview:
        return {
          button1: {
            action: unFlagPost,
            fetching: unflaggingPost,
            varient: "yellow",
            title: "Unflag",
            className:
              "bg-tertiarybuttonbg border-tertiarybuttonborder text-tertiarybuttontext",
          },
          button2: {
            action: disqualifyPost,
            fetching: false,
            title: "Disqualify",
            varient: "red",
            className:
              "bg-rejectbuttonbg border-rejectbuttonborder text-rejectbuttontext",
          },
        };
      default:
        throw new Error("unknown post status");
    }
  }

  if (post.postStatus.status === PostStatus.vendorDraft) {
    return null;
  }
  const actionMetadata = getActionMetadata(post);

  return (
    <div className="flex flex-1 justify-between items-center gap-5 w-full">
      <div className="flex-1 w-full">
        <Button
          className="w-full"
          isLoading={actionMetadata.button1.fetching}
          variant={
            actionMetadata.button1.varient as
              | "red"
              | "yellow"
              | "primary"
              | "secondary"
              | "solid"
              | "boost"
              | "danger"
              | "outline"
          }
          onClick={() => {
            actionMetadata.button1.action();
          }}
        >
          {actionMetadata.button1.title}
        </Button>
      </div>  {actionMetadata.button2 && (
      <div className="flex-1">
      
          <Button
            className="flex-1"
            isLoading={actionMetadata.button2.fetching}
            variant={
              actionMetadata.button2.varient as
                | "red"
                | "yellow"
                | "primary"
                | "secondary"
                | "solid"
                | "boost"
                | "danger"
                | "outline"
            }
            onClick={() => {
              actionMetadata.button2.action();
            }}
          >
            {actionMetadata.button2.title}
          </Button>
     
      </div>   )}
    </div>
  );
};
